import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { SubscriptionAnalyticsBreakdownItem } from "@/types/subscription";
import { Plan, Platform, SubscriptionCycle } from "@/types/enums";

interface SubscriptionPlanDistributionChartProps {
  breakdown: SubscriptionAnalyticsBreakdownItem[];
}

const PLAN_ORDER: Plan[] = [Plan.PLUS, Plan.PRO, Plan.PREMIUM];

const PLAN_COLOR: Record<Plan, string> = {
  [Plan.FREE]: "#9ca3af",
  [Plan.PRO_TRIAL]: "#a855f7",
  [Plan.STUDENT_PASS]: "#22c55e",
  [Plan.PLUS]: "#0ea5e9",
  [Plan.PRO]: "#3b82f6",
  [Plan.PREMIUM]: "#f59e0b",
};

const CYCLE_LABEL: Record<SubscriptionCycle, string> = {
  [SubscriptionCycle.MONTHLY]: "월간",
  [SubscriptionCycle.YEARLY]: "연간",
};

const CYCLE_COLOR: Record<SubscriptionCycle, string> = {
  [SubscriptionCycle.MONTHLY]: "#3b82f6",
  [SubscriptionCycle.YEARLY]: "#10b981",
};

const PLATFORM_NONE_KEY = "NONE";
type PlatformKey = Platform | typeof PLATFORM_NONE_KEY;

const PLATFORM_KEYS: PlatformKey[] = [
  Platform.TOSS,
  Platform.IOS,
  Platform.ANDROID,
  Platform.STRIPE,
  PLATFORM_NONE_KEY,
];

const PLATFORM_COLOR: Record<PlatformKey, string> = {
  [Platform.TOSS]: "#6366f1",
  [Platform.IOS]: "#71717a",
  [Platform.ANDROID]: "#22c55e",
  [Platform.STRIPE]: "#a855f7",
  [PLATFORM_NONE_KEY]: "#d4d4d8",
};

const PLATFORM_LABEL: Record<PlatformKey, string> = {
  [Platform.TOSS]: "TOSS",
  [Platform.IOS]: "IOS",
  [Platform.ANDROID]: "ANDROID",
  [Platform.STRIPE]: "STRIPE",
  [PLATFORM_NONE_KEY]: "없음",
};

interface SeriesDescriptor {
  key: string;
  label: string;
  color: string;
}

interface ChartShape {
  rows: Array<Record<string, string | number>>;
  series: SeriesDescriptor[];
}

function platformKeyOf(platform: Platform | null): PlatformKey {
  return platform ?? PLATFORM_NONE_KEY;
}

function buildChartShape(
  breakdown: SubscriptionAnalyticsBreakdownItem[],
  splitByCycle: boolean,
  splitByPlatform: boolean
): ChartShape {
  const seriesMap = new Map<string, SeriesDescriptor>();
  const planCounts = new Map<Plan, Record<string, number>>();

  for (const plan of PLAN_ORDER) {
    planCounts.set(plan, {});
  }

  for (const row of breakdown) {
    const planMap = planCounts.get(row.plan);
    if (!planMap) continue;

    let seriesKey: string;
    let seriesLabel: string;
    let seriesColor: string;

    if (!splitByCycle && !splitByPlatform) {
      seriesKey = "total";
      seriesLabel = "전체";
      seriesColor = PLAN_COLOR[row.plan];
    } else if (splitByCycle && !splitByPlatform) {
      seriesKey = row.subscriptionCycle;
      seriesLabel = CYCLE_LABEL[row.subscriptionCycle];
      seriesColor = CYCLE_COLOR[row.subscriptionCycle];
    } else if (!splitByCycle && splitByPlatform) {
      const platformKey = platformKeyOf(row.platform);
      seriesKey = platformKey;
      seriesLabel = PLATFORM_LABEL[platformKey];
      seriesColor = PLATFORM_COLOR[platformKey];
    } else {
      const platformKey = platformKeyOf(row.platform);
      seriesKey = `${row.subscriptionCycle}|${platformKey}`;
      seriesLabel = `${CYCLE_LABEL[row.subscriptionCycle]} · ${PLATFORM_LABEL[platformKey]}`;
      seriesColor = PLATFORM_COLOR[platformKey];
    }

    planMap[seriesKey] = (planMap[seriesKey] ?? 0) + row.count;
    if (!seriesMap.has(seriesKey)) {
      seriesMap.set(seriesKey, {
        key: seriesKey,
        label: seriesLabel,
        color: seriesColor,
      });
    }
  }

  const seriesOrder = buildSeriesOrder(splitByCycle, splitByPlatform);
  const series = seriesOrder
    .map((key) => seriesMap.get(key))
    .filter((descriptor): descriptor is SeriesDescriptor => Boolean(descriptor));

  for (const descriptor of seriesMap.values()) {
    if (!series.some((existing) => existing.key === descriptor.key)) {
      series.push(descriptor);
    }
  }

  const seriesKeys = series.map((s) => s.key);
  const rows = PLAN_ORDER.map((plan) => {
    const counts = planCounts.get(plan) ?? {};
    const row: Record<string, string | number> = { plan };
    for (const key of seriesKeys) {
      row[key] = counts[key] ?? 0;
    }
    return row;
  });

  return { rows, series };
}

function buildSeriesOrder(
  splitByCycle: boolean,
  splitByPlatform: boolean
): string[] {
  if (!splitByCycle && !splitByPlatform) return ["total"];
  if (splitByCycle && !splitByPlatform) {
    return [SubscriptionCycle.MONTHLY, SubscriptionCycle.YEARLY];
  }
  if (!splitByCycle && splitByPlatform) return PLATFORM_KEYS as string[];
  const combined: string[] = [];
  for (const cycle of [SubscriptionCycle.MONTHLY, SubscriptionCycle.YEARLY]) {
    for (const platform of PLATFORM_KEYS) {
      combined.push(`${cycle}|${platform}`);
    }
  }
  return combined;
}

export function SubscriptionPlanDistributionChart({
  breakdown,
}: SubscriptionPlanDistributionChartProps) {
  const [splitByCycle, setSplitByCycle] = useState(false);
  const [splitByPlatform, setSplitByPlatform] = useState(false);

  const { rows, series } = useMemo(
    () => buildChartShape(breakdown, splitByCycle, splitByPlatform),
    [breakdown, splitByCycle, splitByPlatform]
  );

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <CardTitle>플랜별 분포</CardTitle>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              id="split-by-cycle"
              checked={splitByCycle}
              onCheckedChange={setSplitByCycle}
            />
            <Label htmlFor="split-by-cycle" className="text-sm">
              주기로 쪼개기
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="split-by-platform"
              checked={splitByPlatform}
              onCheckedChange={setSplitByPlatform}
            />
            <Label htmlFor="split-by-platform" className="text-sm">
              플랫폼으로 쪼개기
            </Label>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-[360px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 16, right: 24, bottom: 8, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="plan" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  fontSize: 12,
                  border: "1px solid #e5e7eb",
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {series.map((descriptor) => (
                <Bar
                  key={descriptor.key}
                  dataKey={descriptor.key}
                  name={descriptor.label}
                  fill={descriptor.color}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
